// openEHR BASE model — BMM package org.openehr.base.foundation_types
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { TYPE_REGISTRY } from "../_shared.ts";
import { rmInitLate } from "../rm_init_late.ts";
import { Boolean, String } from "./primitive_types.ts";

/**
 * Abstract ancestor class for all other classes. Usually maps to a type like \`Any\` or \`Object\` in an object-oriented technology. Defined here to provide value and reference equality semantics.
 */
export abstract class Any {
  /**
   * Optional init is applied only when an argument is passed.
   * `undefined` is a no-op so primitive wrappers can keep calling `super()`.
   * `applyRmInit` is late-bound (`rm_init_late.ts`), so this file does not
   * import `rm/` or `meta/` while it is still evaluating.
   */
  constructor(init?: unknown) {
    if (init === undefined || init === null) return;
    const applyRmInit = rmInitLate.applyRmInit;
    if (!applyRmInit) {
      throw new Error("ehrtslib apply_rm_init.ts is not loaded; import base/mod.ts or openehr_base.ts");
    }
    applyRmInit(this, init);
  }

  /**
   * Value equality: return True if \`this\` and \`_other_\` are attached to objects considered to be equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  abstract is_equal(other: Any): Boolean;

  /**
   * Reference equality for reference types, value equality for value types.
   * @param other - Parameter
   * @returns Result value
   */
  equal(other: Any): Boolean {
    // Reference equality - same object
    return new Boolean(this === other);
  }

  /**
   * Create new instance of a type.
   * Uses the type registry to look up constructors by name.
   * Types must be registered using registerType() before they can be instantiated.
   * 
   * **Security Note:** This method creates instances dynamically based on type name.
   * Do not use with untrusted input as it could instantiate arbitrary registered types.
   * Only use with type names from trusted sources (e.g., parsed from validated openEHR data).
   * 
   * @param a_type - The type name as a String
   * @returns A new instance of the specified type
   * @throws Error if the type is not registered
   */
  instance_of(a_type: String): Any {
    const typeName = a_type?.value?.toUpperCase() || "";
    const constructor = TYPE_REGISTRY.get(typeName);
    if (!constructor) {
      throw new Error(`Unknown type: ${a_type?.value}. Type must be registered using registerType() first.`);
    }
    return new constructor();
  }

  /**
   * Type name of an object as a string. May include generic parameters, as in \`"Interval<Time>"\`.
   * @param an_object - Parameter
   * @returns Result value
   */
  type_of(an_object: Any): String {
    // Get constructor name
    const typeName = an_object.constructor.name;
    return String.from(typeName);
  }

  /**
   * True if current object not equal to \`_other_\`. Returns not \`_equal_()\`.
   * @param other - Parameter
   * @returns Result value
   */
  not_equal(other: Any): Boolean {
    // Use reference equality (not is_equal)
    return new Boolean(!this.equal(other).value);
  }
}
